import { useState, useEffect, useCallback } from "react";
import QRCode from "qrcode";

interface Tag {
  id: string;
  reusable: number;
  status: string;
  printedAt: string | null;
  activatedAt: string | null;
  usedAt: string | null;
  createdAt: string;
}

type StatusFilter = "all" | "unused" | "active" | "used";

export default function TagsPage() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Print batch state
  const [showPrint, setShowPrint] = useState(false);
  const [quantity, setQuantity] = useState("20");
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const [newTags, setNewTags] = useState<Tag[]>([]);

  const [purging, setPurging] = useState(false);
  const [scanBase, setScanBase] = useState("");

  const fetchTags = useCallback(async () => {
    const r = await fetch("/api/tags");
    if (r.ok) setTags(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json())
      .then((d) => setScanBase(d.scanBase));
  }, []);

  function fmt(iso: string | null) {
    return iso ? new Date(iso).toLocaleDateString() : "—";
  }

  const filtered = tags.filter((t) => filter === "all" || t.status === filter);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    const purgeable = filtered.filter((t) => t.status !== "active").map((t) => t.id);
    setSelected(new Set(purgeable));
  }

  async function handlePrintBatch(e: React.FormEvent) {
    e.preventDefault();
    setPrinting(true);
    setPrintError("");
    const r = await fetch("/api/tags/batch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity: Number(quantity) }),
    });
    setPrinting(false);
    if (!r.ok) { const j = await r.json(); setPrintError(j.error ?? "Failed"); return; }
    const created: Tag[] = await r.json();
    setNewTags(created);
    fetchTags();
    printLabels(created);
  }

  async function printLabels(batch: Tag[]) {
    const rows = await Promise.all(batch.map(async (t) => {
      const url = `${scanBase}/scan/${t.id}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 120, margin: 0 });
      return `
        <div style="display:inline-block;width:4.5cm;height:auto;border:1px solid #ccc;
             margin:2px;padding:0;box-sizing:border-box;text-align:center;
             page-break-inside:avoid;vertical-align:top;">
          <img src="${dataUrl}" style="width:120px;height:120px;display:block;margin:0 auto;" />
          <p style="font-family:monospace;font-size:5px;margin:3px 0 1px;">${t.id.slice(0, 12)}</p>
          <p style="font-size:5px;font-weight:bold;margin:1px 0;">Fudge Tracker</p>
          <p style="font-size:4px;color:#444;margin:2px 0 0;line-height:1.4;">
            Stick this tag at the point of change.<br/>
            Scan with your phone to register<br/>
            or close out a temporary change.
          </p>
        </div>`;
    }));

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>QR Labels</title>
      <style>body{margin:8px;} @media print{body{margin:0;}}</style>
      </head><body>${rows.join("")}<script>window.onload=()=>window.print();<\/script></body></html>`);
    w.document.close();
  }

  async function handlePurgeSelected() {
    if (selected.size === 0) return;
    if (!confirm(`Purge ${selected.size} tag(s)? This cannot be undone.`)) return;
    setPurging(true);
    for (const id of selected) {
      await fetch(`/api/tags/${id}`, { method: "DELETE" });
    }
    setPurging(false);
    setSelected(new Set());
    fetchTags();
  }

  async function handleToggleReusable(tag: Tag) {
    await fetch(`/api/tags/${tag.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reusable: !tag.reusable }),
    });
    fetchTags();
  }

  const statusColors: Record<string, string> = {
    unused: "bg-slate-100 text-slate-500",
    active: "bg-green-100 text-green-700",
    used:   "bg-blue-100 text-blue-600",
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tag Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Print, manage, and purge QR tags</p>
        </div>
        <button
          onClick={() => { setShowPrint((v) => !v); setPrintError(""); setNewTags([]); }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
          {showPrint ? "Cancel" : "Print Batch"}
        </button>
      </div>

      {/* Print batch form */}
      {showPrint && (
        <form onSubmit={handlePrintBatch}
          className="bg-white rounded-lg border border-slate-200 p-5 mb-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">Generate & Print Tags</h2>
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Quantity (1–200)</label>
              <input type="number" min={1} max={200} value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-28 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="submit" disabled={printing}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
              {printing ? "Generating…" : "Generate & Print"}
            </button>
          </div>
          {printError && <p className="text-sm text-red-600">{printError}</p>}
          {newTags.length > 0 && (
            <p className="text-sm text-green-700">
              ✓ {newTags.length} tags created — print dialog opened.{" "}
              <button type="button" onClick={() => printLabels(newTags)}
                className="underline text-blue-600">Reprint</button>
            </p>
          )}
        </form>
      )}

      {/* Filters + bulk actions */}
      <div className="flex items-center gap-3 mb-4">
        {(["all", "unused", "active", "used"] as StatusFilter[]).map((s) => (
          <button key={s} onClick={() => { setFilter(s); setSelected(new Set()); }}
            className={`px-3 py-1 text-sm rounded-full ${filter === s ? "bg-blue-600 text-white" : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="flex-1" />
        {selected.size > 0 && (
          <button onClick={handlePurgeSelected} disabled={purging}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
            {purging ? "Purging…" : `Purge ${selected.size} selected`}
          </button>
        )}
        {filtered.some((t) => t.status !== "active") && (
          <button onClick={selectAll}
            className="px-3 py-1 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">
            Select all
          </button>
        )}
      </div>

      {/* Tag table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">No tags found.</p>
        ) : (
          <div className="overflow-auto max-h-[65vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Tag ID</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Printed</th>
                <th className="text-left px-4 py-3">Activated</th>
                <th className="text-left px-4 py-3">Used</th>
                <th className="text-left px-4 py-3">Reusable</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {t.status !== "active" && (
                      <input type="checkbox" checked={selected.has(t.id)}
                        onChange={() => toggleSelect(t.id)}
                        className="rounded border-slate-300" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{t.id}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[t.status] ?? "bg-slate-100 text-slate-500"}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmt(t.printedAt)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmt(t.activatedAt)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmt(t.usedAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleReusable(t)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${t.reusable ? "border-green-400 text-green-700 bg-green-50" : "border-slate-300 text-slate-500"}`}>
                      {t.reusable ? "Yes" : "No"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => printLabels([t])}
                        className="text-xs text-blue-600 hover:underline">
                        Print
                      </button>
                      {t.status === "unused" && (
                        <button
                          onClick={() => { setSelected(new Set([t.id])); handlePurgeSelected(); }}
                          className="text-xs text-red-500 hover:underline">
                          Purge
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
